import { useEffect, useRef, useState } from "react";
import { cutoutFile, cutoutAssetEntry } from "../lib/cutoutFile";
import { resizeFile } from "../lib/resizeFile";
import {
  validateAsset,
  isPublishable,
  keepOriginalAdvice,
  type AssetSpec,
  type ValidationIssue,
} from "@greg-chick/shared";
import { readUpload } from "../lib/imageMeta";
import { bundledArtUrl, effectiveEmoji } from "../lib/effectiveArt";
import { uploadAsset, type ManifestEntry } from "../lib/contentApi";
import { saveBlob, assetFileName, saveAssetUrl } from "../lib/download";
import { ContextPreview } from "./ContextPreview";
import { ActionButton } from "./ActionButton";
import { flipAssetEntry } from "../lib/mirrorImage";
import { loadImage } from "../lib/strip";

/**
 * 🗑 지우기 전에 하는 말.
 *
 * 「지울까요?」만 물으면 **되돌릴 수 있는 줄 안다.** 직접 만든 그림은 다시 만들어야 한다.
 * 그래서 지우기 전에 **덜 무거운 길**을 같이 알려 준다 — 대부분은 그쪽이 하려던 일이다.
 */
export function deletePrompt(label: string): string {
  return [
    `${label} 그림을 지울까요?`,
    "",
    "되돌리려면 다시 올려야 해요.",
    "잠깐만 안 보이게 하려면 [🙈 웹에서 내리기]를 쓰세요.",
  ].join("\n");
}

/** 규격에 걸려 붙잡힌 파일 — 배경을 지우거나 크기를 줄이면 통과한다. */
interface FixInfo {
  file: File;
  dataUrl: string;
  /** 흰 배경이 있어 지워야 한다. */
  bg: boolean;
  /** 이 크기로 줄여야 한다(긴 변 px). */
  max?: number;
}

interface Props {
  slotKey: string;
  /**
   * 🙈 **내리기를 이 자리 하나가 아니라 더 크게 다뤄야 할 때**(캐릭터 하나가 그림 자리를
   * 여럿 쓴다). 주면 [🙈 웹에서 내리기]가 이걸 부른다.
   *
   * 🔴 안 두면 같은 카드에 「이 자리만 내리기」와 「이 아이 통째로 내리기」 **두 버튼이
   * 나란히** 서게 된다 — 이름이 거의 같아서 어느 쪽이 무엇인지 알 수가 없다.
   */
  onSetOnWeb?: (onWeb: boolean) => void;
  label: string;
  spec: AssetSpec;
  entry?: ManifestEntry;
  onChange: (key: string, entry: ManifestEntry | null) => void;
  onRemove?: () => void; // 커스텀 항목 제거(이미지까지 삭제)
  alt?: string; // 한글 라벨 등 — 업로드 시 매니페스트 alt 로 저장(라벨 영속)
  kind?: "image" | "video" | "audio"; // 동영상·음성 파일 업로드 모드
  // "안 올렸을 때 웹이 지금 보여주는 것" — 현재 상태 표시(업로드 전 빈 박스 대신).
  fallbackUrl?: string; // 웹 번들 기본 이미지(웹 출처 절대 URL)
  procedural?: string; // 이미지 없이 코드로 그려지는 슬롯 설명(예: "코드로 그린 기본 배경")
  /**
   * 🎨 **밖에서 건네준 파일**(AI 로 만든 것·붙여넣은 것). 바뀌면 [파일 고르기]와 **같은 길**로 넣는다.
   *
   * 저장하는 길을 따로 만들지 않는 이유: 이 길에는 규격 검사·배경 지우기·자동 줄이기·업로드가
   * 이미 다 붙어 있다. 옆에 두 번째 길을 내면 한쪽에만 검사가 빠지고, 그건 조용히 갈린다.
   */
  incoming?: File | null;
}

// 슬롯 1개: 드래그&드롭/선택 → 규격검사(FR-ADMIN-04) → 미리보기(05) → 매니페스트 갱신.
export function SlotUploader({
  slotKey,
  label,
  spec,
  entry,
  onChange,
  onRemove,
  alt,
  kind = "image",
  fallbackUrl,
  procedural,
  incoming,
  onSetOnWeb,
}: Props) {
  // 안 올렸을 때 **웹이 지금 실제로 보여주는** 번들 그림. 호출부가 따로 주면 그게 우선.
  // 이걸 안 보여주면 화면에는 멀쩡히 나오는 그림이 관리자에선 "비어 있음"으로 보인다.
  /** 🙈 올렸지만 지금은 아이 화면에 안 나가는 상태. 그림은 그대로 있다. */
  const offWeb = !!entry?.offWeb;
  const webFallback = fallbackUrl ?? bundledArtUrl(slotKey);
  const webEmoji = effectiveEmoji(slotKey);
  const isVideo = kind === "video";
  const isAudio = kind === "audio";
  const isMedia = isVideo || isAudio;
  const accept = isAudio ? "audio/*" : isVideo ? "video/*" : "image/*";
  const noun = isAudio ? "음성" : isVideo ? "동영상" : "그림"; // 배지·안내 문구용
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /**
   * 검사에 걸렸지만 **여기서 고칠 수 있는** 파일. 그냥 막기만 하면 운영자는 갇힌다 —
   * AI 로 뽑은 그림은 **늘 흰 배경이고 늘 크다**(1024~2048px).
   *
   * 둘 다 걸리는 일이 흔해서 **한 번에** 처리한다. 두 번 누르게 하면 그것도 막힌 것이다.
   * 순서: 배경을 먼저 지우고(여백이 잘려 그림이 커진다) → 그 다음에 줄인다.
   */
  const [fix, setFix] = useState<FixInfo | null>(null);
  const [fixing, setFixing] = useState(false);
  /**
   * 🖨️ **줄이면서 사라질 뻔한 인쇄용 원본.**
   *
   * AI 로 뽑은 그림은 1024~2048px 로 나오는데 화면용 상한은 512px 이다. 그대로 줄이면
   * 굿즈(키링 50mm·300dpi ≒ 600px)에 쓸 수 있던 그림이 **조용히 사라진다.**
   * 운영자는 「올렸다」고만 알고, 나중에 굿즈를 만들려 할 때 그때 안다 — 되살릴 방법은 없다.
   *
   * 그래서 줄이기 **직전 파일을 붙들어 두고** 내려받게 한다. 판정은 shared 한 곳(printArt).
   */
  const [keepOrig, setKeepOrig] = useState<{ file: File; name: string; text: string } | null>(null);

  /**
   * **버튼 한 번이면 고칠 수 있는 것**과 **정말 안 되는 것**을 가른다.
   * 문구로 판별하면 문구를 다듬는 순간 갈리므로 `code` 로 본다(shared 의 ValidationCode).
   */
  const FIXABLE = new Set(["background", "long_edge"]);
  const fixableIssues = fix ? issues.filter((i) => i.code && FIXABLE.has(i.code)) : [];
  const hardIssues = fix ? issues.filter((i) => !(i.code && FIXABLE.has(i.code))) : issues;
  /**
   * 🔴 **버튼을 눌러도 남는 오류가 있으면 「한 번이면 됩니다」 는 거짓말이다.**
   * (예: JPG 는 형식 자체가 안 맞아서, 배경을 지우고 줄여도 여전히 못 올린다.)
   * 그럴 땐 초록 안내를 감추고 빨간 ✗ 만 보여 준다 — 다른 파일이 필요하다는 뜻이라서다.
   */
  const canFix = fix !== null && !hardIssues.some((i) => i.level === "error");

  /** 같은 파일을 두 번 넣지 않는다 — 렌더마다 다시 올리면 R2 에 같은 것이 쌓인다. */
  const tookRef = useRef<File | null>(null);
  /** 이번에 건네받은 그림이 실제로 들어갔나 — 안 들어갔으면 그 자리에서 말해 준다. */
  const appliedRef = useRef(false);
  /** 안 들어갔을 때 이 칸을 화면 안으로 끌어오기 위해. */
  const boxRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!incoming || tookRef.current === incoming) return;
    tookRef.current = incoming;
    appliedRef.current = false;
    /**
     * 🔴 **우리가 만든 파일은 우리가 다듬는다.**
     *
     * AI 그림은 1024px 로 오는데 자리는 512px(친구는 256px)까지만 받는다. 예전엔 여기서
     * 붙잡혀 [한 번에 고치기] 버튼이 뜰 뿐이었다 — 누르기 전엔 안 들어가니 운영자에게는
     * **「적용이 안 된다」**로만 보인다(2026-08-23 신고). 파일을 고른 게 아니라
     * **우리가 만들어 건넨 것**이라 되묻는 것 자체가 이상하다.
     *
     * 그래서 [파일 고르기]와 같은 길로 넣되, 걸리면 **그 자리에서 자동으로 고쳐** 다시 넣는다.
     * 고쳐도 안 되는 것(형식 자체가 다름)은 그대로 빨간 ✗ 로 남는다.
     */
    void (async () => {
      // 🔴 **상태를 기다리지 않는다.** handle 이 setFix 를 부르지만 그 값은 다음 렌더에나
      //    보인다 — 여기서 state 를 읽으면 늘 null 이라 자동 고치기가 한 번도 안 돈다.
      //    그래서 handle 이 「붙잡힌 것」을 **돌려주고**, 그걸 그대로 고친다.
      const caught = await handle(incoming);
      if (caught) await applyFix(caught);
      /**
       * 🔴 **안 들어갔으면 말해야 한다.**
       *
       * AI 가 배경을 안 지운 채 그림을 주면 줄이기로는 못 고친다. 그런데 운영자는
       * **위쪽 [✅ 이 그림 쓰기]** 를 누른 상태라, 아래 칸의 빨간 ✗ 는 눈에 안 들어온다 —
       * 「적용이 안 된다」로만 보이고 이유를 알 길이 없다(2026-08-23 신고).
       *
       * 그래서 안 들어갔으면 그 칸을 **화면 안으로 끌어와** 보여 준다.
       */
      if (!appliedRef.current) {
        setNote(
          "⚠️ 이 그림은 그대로 못 써요 — 아래 빨간 표시를 봐 주세요(배경이 안 지워졌거나 형식이 달라요).",
        );
        // 끌어오기는 **곁들이는 것**이다 — 없는 환경(jsdom·옛 브라우저)에서 이것 때문에
        // 안내 자체가 안 뜨면 본말이 뒤집힌다.
        boxRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      }
    })();
    // handle 은 렌더마다 새 함수라 의존성에 넣으면 계속 다시 돈다 — 파일이 바뀔 때만 본다.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handle·applyFix 는 렌더마다 새 함수다 — 넣으면 끌어온 파일 안내가 끝없이 다시 뜬다
  }, [incoming]);

  /**
   * 파일 하나를 넣는다. 규격에 걸려 **붙잡혔으면 그 정보를 돌려준다**(안 걸렸으면 null).
   * 돌려주는 이유: 부르는 쪽이 상태 갱신을 기다리지 않고 바로 이어서 고칠 수 있게.
   */
  async function handle(file: File): Promise<FixInfo | null> {
    setBusy(true);
    setNote(null);
    try {
      const meta = await readUpload(file);
      const found = validateAsset(spec, meta);
      setIssues(found);
      // 여기서 고칠 수 있는 것이면 붙잡아 둔다(흰 배경 · 규격 초과)
      const bg = meta.hasBackground === true && meta.edgeIsWhite === true;
      const long = Math.max(meta.width ?? 0, meta.height ?? 0);
      const over = spec.maxLongEdgePx && long > spec.maxLongEdgePx ? spec.maxLongEdgePx : undefined;
      const caught: FixInfo | null =
        !isPublishable(found) && (bg || over)
          ? { file, dataUrl: meta.dataUrl, bg, max: over }
          : null;
      setFix(caught);
      // 고쳐도 안 되는 것이 섞여 있으면 고칠 대상이 아니다(형식 자체가 다른 경우).
      const fixable =
        caught && !found.some((i) => i.level === "error" && !(i.code && FIXABLE.has(i.code)));
      if (!isPublishable(found)) return fixable ? caught : null;
      // 원본은 R2 업로드(매니페스트엔 URL만). 실패 시 dataURL 임베드 폴백(오프라인/내보내기).
      const keepAlt = entry?.alt ?? alt;
      try {
        const url = await uploadAsset(slotKey, file);
        /**
         * 🔴 **관리자에서 볼 수 있는 주소로 바꿔서 넣는다.**
         *
         * 업로드는 `/assets/…` 상대 주소를 돌려주는데, 그건 **엣지 출처**의 경로다.
         * 관리자는 다른 출처(admin.…)라 그대로 넣으면 그림을 못 찾는다 —
         * 화면에는 "올라갔어요"라고 뜨는데 **미리보기만 안 바뀌고**, 새로고침해야 보였다.
         * (새로고침하면 `loadManifest` 가 절대 주소로 바꿔 주기 때문에 그제야 나온다.)
         *
         * 상태에 들어가는 형태를 `loadManifest` 와 **같게** 맞춘다 — 게시할 때
         * `publishAssets` 가 다시 상대 주소로 되돌리므로 저장되는 값은 그대로다.
         */
        appliedRef.current = true;
        onChange(slotKey, { url, alt: keepAlt });
        setNote("✓ 올라갔어요 — 위의 [🚀 웹에 올리기]를 눌러야 웹에 보여요");
      } catch {
        appliedRef.current = true;
        onChange(slotKey, { url: meta.dataUrl, alt: keepAlt });
        setNote("서버에 연결이 안 돼 임시로만 넣었어요(서버 켠 뒤 다시 올려주세요)");
      }
      return null; // 여기까지 왔으면 잘 들어갔다 — 고칠 것이 없다
    } finally {
      setBusy(false);
    }
  }

  /** 걸린 것들을 한 번에 고치고 다시 업로드한다. 배경 → 크기 순서다. */
  async function doFix() {
    if (fix) await applyFix(fix);
  }

  /** 붙잡힌 정보를 받아 고친다 — 상태를 안 거치므로 넣자마자 이어서 부를 수 있다. */
  async function applyFix(fix: FixInfo) {
    setFixing(true);
    setNote(null);
    try {
      let file = fix.file;
      let dataUrl = fix.dataUrl;
      const did: string[] = [];

      if (fix.bg) {
        const r = await cutoutFile(file, dataUrl);
        if (!r) {
          setNote("배경을 못 지웠어요 — 단순한 흰색이 아닐 수 있어요. 개발자에게 알려주세요");
          return;
        }
        file = r.file;
        dataUrl = r.dataUrl;
        did.push(`배경 지움(${Math.round(r.removedRatio * 100)}%)`);
      }
      if (fix.max) {
        // 배경을 지우면 여백이 잘려 그림이 커진다 — 그래서 **줄이기는 마지막**이다
        const before = file; // 🖨️ 줄이기 전 파일 — 인쇄용으로 남길 수 있다
        const r = await resizeFile(file, dataUrl, fix.max);
        if (r) {
          file = r.file;
          did.push(`${r.from.width}→${r.to.width}px 로 줄임`);
          // 🖨️ 인쇄에 쓸 수 있던 크기였는데 화면용으로 줄었으면 **원본을 지킬 기회를 준다**
          const advice = keepOriginalAdvice(r.from, r.to);
          setKeepOrig(
            advice
              ? {
                  file: before,
                  // 이름 규칙은 **받기 한 곳**(download.ts)에 둔다 — 여기서 또 만들면 갈린다
                  name: assetFileName(`${slotKey}-원본`, "", before.type),
                  text: advice.text,
                }
              : null,
          );
        }
      }
      if (did.length === 0) {
        setNote("고칠 수 있는 게 없었어요 — 파일을 다시 확인해 주세요");
        return;
      }
      setNote(`${did.join(" · ")}. 이어서 올릴게요…`);
      setFix(null);
      await handle(file);
    } finally {
      setFixing(false);
    }
  }

  return (
    <div className="slot" data-slot={slotKey} ref={boxRef}>
      <div className="slot__head">
        <span className="slot__label">{label}</span>
        {/* 🙈 「웹에서 내림」은 **가장 먼저** 말해야 한다 — 그림이 멀쩡히 보이는데 아이 화면에는
            없는 상태라, 안 적으면 「올렸는데 왜 안 나오지」로 되돌아온다. */}
        <span
          className={`slot__badge${offWeb ? " slot__badge--off" : entry ? " slot__badge--real" : webFallback || webEmoji || procedural ? " slot__badge--web" : ""}`}
          title={
            offWeb
              ? "그림은 그대로 있어요. 지금은 아이 화면에 안 나와요"
              : entry
                ? `올린 ${noun}이 웹에 보여요`
                : webFallback
                  ? `지금 웹에 이 기본 ${noun}이 보여요`
                  : procedural
                    ? "지금 웹에 자동으로 그려진 모양이 보여요"
                    : `아직 아무 ${noun}도 없어요`
          }
        >
          {offWeb
            ? "🙈 웹에 안 보임"
            : entry
              ? `✓ ${noun} 있음`
              : webFallback
                ? `기본 ${noun}`
                : webEmoji
                  ? "이모지"
                  : procedural
                    ? "자동 그림"
                    : "비어 있음"}
        </span>
      </div>

      <div
        className={`slot__drop${over ? " slot__drop--over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) void handle(f);
        }}
      >
        {entry ? (
          isAudio ? (
            <audio className="slot__preview slot__preview--audio" src={entry.url} controls />
          ) : isVideo ? (
            <video className="slot__preview" src={entry.url} controls muted playsInline />
          ) : (
            <img className="slot__preview" src={entry.url} alt={`${label}`} />
          )
        ) : webFallback && !isMedia ? (
          <div className="slot__ghost">
            <img
              className="slot__preview slot__preview--ghost"
              src={webFallback}
              alt={`${label} 웹 기본 그림`}
            />
            <span className="slot__ghost-cap">기본 그림 — 새 그림을 올리면 바뀌어요</span>
          </div>
        ) : webEmoji ? (
          // 그림이 없을 때 화면에 **실제로 나오는 이모지**를 그대로 보여준다.
          // 회색 자리표시자를 그리면 "무엇이 나오고 있는지"를 알 수 없다.
          <div className="slot__emoji">
            <span className="slot__emoji-ico" aria-hidden="true">
              {webEmoji}
            </span>
            <span className="slot__ghost-cap">
              지금 화면에 이 이모지가 나와요 — 그림을 올리면 바뀌어요
            </span>
          </div>
        ) : procedural ? (
          <div className="slot__proc">
            <span className="slot__proc-ico" aria-hidden="true">
              🖥️
            </span>
            <span>{procedural}</span>
            <span className="slot__proc-sub">그림을 올리면 이 자리에 대신 보여요</span>
          </div>
        ) : (
          <p className="slot__hint">
            여기로 {isAudio ? "음성 파일을" : isVideo ? "동영상 파일을" : "이미지를"} 끌어다 놓거나
            선택
          </p>
        )}
        <label className="slot__file">
          {busy ? "확인 중…" : "파일 고르기"}
          <input
            type="file"
            accept={accept}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handle(f);
            }}
          />
        </label>
      </div>

      <div className="slot__spec">
        허용: {spec.formats.join("/")}
        {spec.maxKB ? ` · ≤${spec.maxKB}KB` : ""}
        {spec.maxLongEdgePx ? ` · 긴변 ≤${spec.maxLongEdgePx}px` : ""}
        {spec.tileGrid ? ` · ${spec.tileGrid}px 배수` : ""}
      </div>

      {hardIssues.length > 0 ? (
        <ul className="slot__issues">
          {hardIssues.map((i, n) => (
            <li key={n} className={`slot__issue slot__issue--${i.level}`}>
              {i.level === "error" ? "✗" : "⚠️"} {i.message}
            </li>
          ))}
        </ul>
      ) : null}

      {/*
        막기만 하면 운영자는 갇힌다 — AI(Google AI Studio·ChatGPT)로 뽑은 그림은
        **늘 흰 배경**이라서다. 그래서 막는 자리에 바로 고칠 버튼을 둔다.
      */}
      {canFix && fix ? (
        <div className="slot__fix">
          <b className="slot__fix-title">
            {fix.bg && fix.max ? "🩹📐" : fix.bg ? "🩹" : "📐"} 이대로는 못 올려요 — 아래 버튼 한
            번이면 됩니다
          </b>
          <ul className="slot__fix-why">
            {fixableIssues.map((i, n) => (
              <li key={n}>{i.message.replace(/ — .*$/, "")}</li>
            ))}
          </ul>
          <button
            type="button"
            className="slot__fix-btn"
            onClick={() => void doFix()}
            disabled={fixing}
          >
            {fixing
              ? "고치는 중…"
              : fix.bg && fix.max
                ? "배경 지우고 규격에 맞추기"
                : fix.bg
                  ? "배경 지우기"
                  : "규격에 맞게 줄이기"}
          </button>
          <span className="slot__fix-hint">
            {fix.bg && fix.max
              ? `흰 배경을 지우고 긴 변 ${fix.max}px 로 줄여서 다시 올려요`
              : fix.bg
                ? "흰 배경을 지우고 여백까지 잘라서 다시 올려요"
                : `긴 변 ${fix.max}px 로 줄여서 다시 올려요 — 원본은 assets-src 에 따로 남기세요`}
          </span>
        </div>
      ) : null}

      {note ? <p className="slot__note">{note}</p> : null}

      {/* 🖨️ **원본이 사라지기 전에 붙잡는 자리.** 안 띄우면 운영자는 사라진 줄도 모른다. */}
      {keepOrig ? (
        <div className="slot__keep">
          <p className="slot__keep-text">🖨️ {keepOrig.text}</p>
          <div className="slot__keep-row">
            <button
              type="button"
              className="slot__keep-save"
              onClick={() => {
                saveBlob(keepOrig.file, keepOrig.name);
                setNote(`원본을 받았어요 — ${keepOrig.name}. 굿즈 만들 때 이 파일을 쓰세요`);
                setKeepOrig(null);
              }}
            >
              💾 인쇄용 원본 저장
            </button>
            <button type="button" className="slot__keep-skip" onClick={() => setKeepOrig(null)}>
              안 받을래요
            </button>
          </div>
        </div>
      ) : null}

      {entry ? (
        <div className="slot__actions">
          {!isMedia ? (
            <button type="button" className="slot__preview-btn" onClick={() => setPreviewing(true)}>
              📍 어디에 보일까?
            </button>
          ) : null}
          {/* ⬇ **내 컴퓨터로.** 올린 그림을 다시 꺼낼 길이 없으면, 인쇄에 쓰거나 다른 도구로
              다듬으려 할 때마다 원본 파일을 어디 뒀는지 찾아 헤매게 된다. 자리마다 둔다 —
              어느 메뉴에 있든 「지금 이 칸에 든 그림」을 바로 받을 수 있어야 한다. */}
          <ActionButton
            className="slot__preview-btn"
            title="이 칸에 든 그림을 내 컴퓨터에 저장해요"
            onAction={() => saveAssetUrl(slotKey, entry.url)}
            doneLabel="저장했어요"
          >
            ⬇ 내려받기
          </ActionButton>
          {/* ↔ **반대로 들어온 그림을 그 자리에서 고친다.**
              AI 가 그린 옆모습은 반쯤 확률로 반대를 본다. 예전엔 캐릭터 화면에만 이 단추가
              있어서 배경·소품·굿즈는 저장소 파일을 고쳐 배포해야 했다 — 운영자가 못 하는 일이다.
              🔴 칸이 이어진 스트립은 **칸마다 제자리에서** 뒤집는다(통째로 뒤집으면 걸음이 뒤로 감긴다). */}
          {/* 🪄 **배경을 투명하게.** 예전엔 **올릴 때만** 지울 수 있었다(규격에 걸린 파일).
              그래서 이미 올라간 그림에 배경이 남아 있으면 운영자가 손댈 방법이 없었다 —
              지우고 다시 올리거나 개발자에게 말하는 수밖에(그레그 흰색 발밑이 그 경우다).

              🔴 **자르지 않는다.** 걷기 스트립은 칸이 가로로 이어져 있어서 여백을 자르면
              칸 나누기가 통째로 어긋난다(걸음이 깨진다). 판정은 `shared/cutout.ts` 한 곳. */}
          {!isMedia ? (
            <ActionButton
              className="slot__preview-btn"
              title="테두리에 붙은 배경을 투명하게 만들어요. 안쪽의 같은 색(흰 셔츠 등)은 남아요. 되돌리려면 [↩ 아까 그림으로]"
              onAction={async () => {
                const r = await cutoutAssetEntry(slotKey, entry.url, {
                  loadImage,
                  upload: uploadAsset,
                });
                onChange(slotKey, { ...entry, url: r.url, prev: r.prev });
                // 🔴 안 된 것을 안 됐다고 말한다 — 조용히 넘어가면 그대로 게시된다
                if (r.warning) setNote(r.warning);
                else setNote(`배경을 지웠어요 (${Math.round(r.ratio * 100)}%).`);
              }}
              doneLabel="지웠어요"
            >
              🪄 배경 지우기
            </ActionButton>
          ) : null}
          {!isMedia ? (
            <ActionButton
              className="slot__preview-btn"
              title="그림을 좌우로 뒤집어요. 되돌리려면 [↩ 아까 그림으로]"
              onAction={async () => {
                const next = await flipAssetEntry(slotKey, entry.url, {
                  loadImage,
                  upload: uploadAsset,
                });
                onChange(slotKey, { ...entry, ...next });
              }}
              doneLabel="뒤집었어요"
            >
              ↔ 좌우 뒤집기
            </ActionButton>
          ) : null}
          {entry.prev ? (
            <button
              type="button"
              className="slot__preview-btn"
              title="직전에 게시했던 파일로 되돌리기(다시 누르면 재전환)"
              onClick={() => onChange(slotKey, { ...entry, url: entry.prev!, prev: entry.url })}
            >
              ↩ 아까 그림으로
            </button>
          ) : null}
          {/* 🙈 **지우지 않고 빼는 길.** 이게 없으면 「이번 행사 끝나면 내리자」·「품절이라
              잠깐 빼자」에 지우기를 쓰게 된다 — 직접 만든 그림은 다시 만들어야 한다. */}
          <button
            type="button"
            className="slot__preview-btn"
            title={
              offWeb
                ? "다시 아이 화면에 나오게 해요"
                : "그림은 그대로 두고 아이 화면에서만 빼요. 언제든 다시 올릴 수 있어요"
            }
            // 값은 **「웹에 올릴까」**로 넘긴다. 「내릴까」로 넘기면 부르는 쪽과 뜻이 뒤집혀
            // 눌러도 아무 일이 안 일어난다(실제로 그렇게 짰다가 테스트에서 잡혔다).
            onClick={() =>
              onSetOnWeb
                ? onSetOnWeb(offWeb)
                : onChange(slotKey, { ...entry, offWeb: offWeb ? undefined : true })
            }
          >
            {offWeb ? "👁 다시 올리기" : "🙈 웹에서 내리기"}
          </button>
          <button
            type="button"
            className="slot__remove"
            title="그림을 아주 지워요. 되돌리려면 다시 올려야 해요"
            onClick={() => {
              if (!window.confirm(deletePrompt(label))) return;
              onChange(slotKey, null);
              setIssues([]);
            }}
          >
            🗑 지우기
          </button>
        </div>
      ) : null}

      {onRemove ? (
        <button type="button" className="slot__del-item" onClick={onRemove}>
          ✕ 항목 제거
        </button>
      ) : null}

      {previewing && entry ? (
        <div className="cp-backdrop" onClick={() => setPreviewing(false)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-modal__head">
              <b>{label}</b>
              <button
                type="button"
                className="cp-modal__close"
                aria-label="닫기"
                title="닫기"
                onClick={() => setPreviewing(false)}
              >
                ✕
              </button>
            </div>
            <ContextPreview slotKey={slotKey} url={entry.url} entry={entry} onChange={onChange} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
